/**
 * Reports main view component - with working upload functionality
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useToast, ToastContainer } from '../common/Toast';
import reportService from '../../services/reportService';
import ReportDetailView from './ReportDetailView';

const ReportsView = () => {
  const { state } = useAppContext();
  const { toasts, showToast, removeToast } = useToast();
  
  // Local state
  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [scrapingStatus, setScrapingStatus] = useState({});

  // Load reports
  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reportService.getReports({ limit: 100, skip: 0 });
      console.log('Load reports response:', response);
      
      if (response.success) {
        // After API interceptor transformation, data is at response.reports
        const reportsList = response.reports || response.data?.reports || [];
        const total = response.total || response.data?.total || 0;
        
        console.log('Reports found:', reportsList.length, 'Total:', total);
        setReports(reportsList);
        setTotalCount(total);
      } else {
        console.error('Failed to load reports:', response);
        setReports([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      setReports([]);
      setTotalCount(0);  
    } finally {
      setLoading(false);
    }
  }, []);

  // Load reports on mount
  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Handle file upload or URL scraping
  const handleFileUpload = useCallback(async (uploadType, data) => {
    try {
      showToast(`${uploadType === 'file' ? 'Uploading file' : 'Scraping URL'}...`, 'info');
      
      let response;
      
      if (uploadType === 'file') {
        const { file, reportName, reportDescription, keyFindings, personName, jobDescription, companyName } = data;
        console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);
        console.log('Form data:', { reportName, reportDescription, keyFindings, personName, jobDescription, companyName });
        
        response = await reportService.uploadFile(file, reportName, reportDescription, keyFindings, personName, jobDescription, companyName);
      } else {
        const { url, reportName, reportDescription, personName, jobDescription, companyName } = data;
        console.log('Scraping URL:', url);
        console.log('Form data:', { reportName, reportDescription, personName, jobDescription, companyName });
        
        response = await reportService.scrapeUrl(url, reportName, reportDescription, personName, jobDescription, companyName);
      }
      
      console.log('Upload/Scrape response:', response);
      
      if (response && response.success) {
        showToast(`✅ ${uploadType === 'file' ? 'File uploaded' : 'URL scraped'} successfully!`, 'success');
        setUploadModalOpen(false);
        loadReports(); // Refresh list
      } else {
        const errorMsg = response?.error || response?.message || `Unknown ${uploadType} error`;
        console.error(`${uploadType} failed:`, errorMsg);
        showToast(`❌ ${uploadType === 'file' ? 'Upload' : 'Scraping'} failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error(`Error ${uploadType === 'file' ? 'uploading file' : 'scraping URL'}:`, error);
      
      // Extract meaningful error message
      let errorMessage = 'Unknown error';
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 422) {
          if (data.detail && Array.isArray(data.detail)) {
            const validationErrors = data.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
            errorMessage = `Validation error: ${validationErrors}`;
          } else if (data.detail) {
            errorMessage = `Validation error: ${data.detail}`;
          } else {
            errorMessage = 'Form validation failed';
          }
        } else {
          errorMessage = data?.detail || data?.message || `Server error: ${status}`;
        }
      } else if (error.request) {
        errorMessage = 'No response from server - check connection';
      } else {
        errorMessage = error.message || 'Request setup error';
      }
      
      showToast(`❌ ${uploadType === 'file' ? 'Upload' : 'Scraping'} error: ${errorMessage}`, 'error');
    }
  }, [loadReports, showToast]);

  // Handle scrape document
  const handleScrape = useCallback(async (reportId) => {
    try {
      setScrapingStatus(prev => ({ ...prev, [reportId]: true }));
      showToast('Scraping document with Claude Sonnet 4.0...', 'info');
      
      const response = await reportService.scrapeDocument(reportId);
      
      if (response && response.success) {
        showToast('✅ Document scraped successfully!', 'success');
        loadReports(); // Refresh list to show updated status
      } else {
        const errorMsg = response?.error || response?.message || 'Scraping failed';
        showToast(`❌ Scraping failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error scraping document:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Scraping error: ${errorMessage}`, 'error');
    } finally {
      setScrapingStatus(prev => ({ ...prev, [reportId]: false }));
    }
  }, [loadReports, showToast]);

  // Handle view report details
  const handleViewReport = (report) => {
    setSelectedReport(report);
  };

  // Handle back from detail view
  const handleBackFromDetail = () => {
    setSelectedReport(null);
    loadReports(); // Refresh list when returning
  };

  // Handle report update from detail view
  const handleReportUpdate = (updatedReport) => {
    setReports(prev => prev.map(report => 
      report.id === updatedReport.id ? updatedReport : report
    ));
  };

  // If viewing a specific report, show detail view
  if (selectedReport) {
    return (
      <ReportDetailView
        report={selectedReport}
        onBack={handleBackFromDetail}
        onUpdate={handleReportUpdate}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Data / Reports</h1>
            <p className="text-sm text-gray-600">
              Upload and manage reports for article generation
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setUploadModalOpen(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Add Report</span>
            </button>
            <button
              onClick={loadReports}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading reports...</p>
            </div>
          </div>
        ) : reports && reports.length > 0 ? (
          <div className="h-full flex flex-col">
            {/* Report List Header */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center text-xs font-medium text-gray-700">
              <div className="w-4 flex-shrink-0 mr-3"></div>
              <div className="w-8 flex-shrink-0 mr-3"></div>
              <div className="flex-1 min-w-0 mr-4">Report Name</div>
              <div className="w-32 flex-shrink-0 mr-4">Company</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Scraped</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Feature Article</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Perspective Articles</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Press Release</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Blogs</div>
              <div className="w-32 text-center flex-shrink-0">Action</div>
            </div>
            
            {/* Report Rows */}
            <div className="flex-1 overflow-auto bg-white">
              {reports.map((report) => (
                <div key={report.id} className="px-4 py-3 border-b border-gray-200 flex items-center hover:bg-gray-50">
                  {/* Checkbox */}
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300 flex-shrink-0 mr-3" />
                  
                  {/* File Icon */}
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3">
                    <span className="text-xl">📄</span>
                  </div>
                  
                  {/* Name Column */}
                  <div className="flex-1 min-w-0 mr-4">
                    <div 
                      className="text-sm font-semibold text-blue-600 hover:text-blue-800 cursor-pointer truncate"
                      onClick={() => handleViewReport(report)}
                    >
                      {report.report_name}
                    </div>
                    <div className="text-xs text-gray-600 truncate">
                      {report.file_name} • {report.report_description?.substring(0, 50)}...
                    </div>
                  </div>
                  
                  {/* Company Column */}
                  <div className="w-32 flex-shrink-0 mr-4">
                    <div className="text-sm text-gray-900 truncate">
                      {report.company_name || '-'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {report.person_name || ''}
                    </div>
                  </div>
                  
                  {/* Status Columns */}
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    {report.content_extracted ? (
                      <span className="text-green-600 text-lg" title="Content extracted">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg" title="Not extracted">✗</span>
                    )}
                  </div>
                  
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    {report.article_drafted ? (
                      <span className="text-green-600 text-lg" title="Article drafted">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg" title="No article yet">✗</span>
                    )}
                  </div>
                  
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    <span className="text-red-500 text-lg" title="Not available yet">✗</span>
                  </div>
                  
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    <span className="text-red-500 text-lg" title="Not available yet">✗</span>
                  </div>
                  
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    <span className="text-red-500 text-lg" title="Not available yet">✗</span>
                  </div>
                  
                  {/* Action Button */}
                  <div className="w-32 flex items-center justify-center flex-shrink-0">
                    {!report.content_extracted ? (
                      <button 
                        onClick={() => handleScrape(report.id)}
                        disabled={scrapingStatus[report.id]}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:bg-blue-400 flex items-center space-x-1"
                      >
                        {scrapingStatus[report.id] ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                            <span>Scraping...</span>
                          </>
                        ) : (
                          <span>Scrape</span>
                        )}
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleViewReport(report)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Results Summary */}
            <div className="px-4 py-2 bg-white border-t border-gray-200">
              <div className="text-xs text-gray-700">
                Showing {reports.length} of {totalCount} reports
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <svg className="w-16 h-16 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-medium mb-2">No reports found</h3>
              <p className="text-center mb-4">Get started by uploading your first report file.</p>
              
              <button
                onClick={() => setUploadModalOpen(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
              >
                Upload Your First Report
              </button>
              
              <div className="bg-white border border-gray-200 p-4 rounded-lg max-w-md mx-auto mt-6">
                <h4 className="font-medium mb-2">Same Status Columns as Interviews:</h4>
                <div className="flex justify-center space-x-4 text-xs">
                  <span>✓ Scraped</span>
                  <span>✓ Feature Article</span>
                  <span>✗ Perspective Articles</span>
                  <span>✗ Press Release</span>
                  <span>✗ Blogs</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <ReportUploadModal
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onUpload={handleFileUpload}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

// Upload Modal Component
const ReportUploadModal = ({ isOpen, onClose, onUpload }) => {
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'url'
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [keyFindings, setKeyFindings] = useState('');
  
  // New optional fields
  const [personName, setPersonName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [companyName, setCompanyName] = useState('');
  
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadType === 'file') {
      if (!file || !reportName || !reportDescription) {
        return;
      }
    } else {
      if (!url || !reportName || !reportDescription) {
        return;
      }
    }

    setUploading(true);
    try {
      if (uploadType === 'file') {
        await onUpload('file', {
          file,
          reportName,
          reportDescription,
          keyFindings,
          personName,
          jobDescription,
          companyName
        });
      } else {
        await onUpload('url', {
          url,
          reportName,
          reportDescription,
          personName,
          jobDescription,
          companyName
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    
    // Auto-populate report name from filename if empty
    if (selectedFile && !reportName) {
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "");
      setReportName(nameWithoutExt);
    }
  };

  const handleUrlChange = (e) => {
    const enteredUrl = e.target.value;
    setUrl(enteredUrl);
    
    // Auto-populate report name from URL if empty
    if (enteredUrl && !reportName) {
      try {
        const urlObj = new URL(enteredUrl);
        const pathParts = urlObj.pathname.split('/').filter(part => part);
        const lastPart = pathParts[pathParts.length - 1] || urlObj.hostname;
        setReportName(lastPart);
      } catch {
        // Invalid URL, ignore
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Add Report</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Upload Type Tabs */}
        <div className="flex mb-4 border-b">
          <button
            type="button"
            onClick={() => setUploadType('file')}
            className={`px-4 py-2 text-sm font-medium ${
              uploadType === 'file'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Upload File
          </button>
          <button
            type="button"
            onClick={() => setUploadType('url')}
            className={`px-4 py-2 text-sm font-medium ${
              uploadType === 'url'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            From URL
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {uploadType === 'file' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File (PDF, DOC, CSV, TXT) *
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.csv,.txt"
                onChange={handleFileChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                required
                disabled={uploading}
              />
              {file && (
                <p className="text-xs text-gray-500 mt-1">
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL *
              </label>
              <input
                type="url"
                value={url}
                onChange={handleUrlChange}
                placeholder="https://example.com/report"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                required
                disabled={uploading}
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Name *
            </label>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Enter a descriptive name for this report"
              required
              disabled={uploading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brief Description *
            </label>
            <textarea
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Describe what this report contains and its purpose"
              required
              disabled={uploading}
            />
          </div>
          
          {/* Optional person/company fields */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Optional Information</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Person Name
                </label>
                <input
                  type="text"
                  value={personName}
                  onChange={(e) => setPersonName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Associated person"
                  disabled={uploading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Company name"
                  disabled={uploading}
                />
              </div>
            </div>
            
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Description
              </label>
              <input
                type="text"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Job title or role"
                disabled={uploading}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key Findings <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={keyFindings}
              onChange={(e) => setKeyFindings(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Leave blank - will be populated after AI processing"
              disabled={uploading}
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors text-sm flex items-center justify-center"
              disabled={uploading || (uploadType === 'file' ? (!file || !reportName || !reportDescription) : (!url || !reportName || !reportDescription))}
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b border-white mr-2"></div>
                  {uploadType === 'file' ? 'Uploading...' : 'Scraping...'}
                </>
              ) : (
                uploadType === 'file' ? 'Upload Report' : 'Add from URL'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportsView;